import { useEffect } from 'react'
import { Link, Outlet, useLocation, useParams } from 'react-router-dom'
import { ErrorBoundary, PermissionProvider, ToastContainer, applyColourScheme } from '@/kit/ui'
import { useDevStore } from '@/store/devStore'
import { useHubStore } from '@/store/hubStore'
import type { Lender } from '@/types'
import { useCurrentUser } from './useCurrentUser'

/**
 * The work pages that take the whole window (owner, 2026-10-17: "canvas a separate page", the playground "like VS
 * Code"): no sidebar and no top bar, each page draws its own title bar with a way back. Same permissions and toasts.
 */
export function FullPage() {
  const location = useLocation()
  const { user, can } = useCurrentUser()
  const scheme = useDevStore((s) => s.colourScheme)
  useEffect(() => applyColourScheme(scheme), [scheme])
  return (
    <PermissionProvider value={{ can, roleLabel: user.role }}>
      <ErrorBoundary key={location.pathname}>
        <Outlet />
      </ErrorBoundary>
      <ToastContainer />
    </PermissionProvider>
  )
}

/** The lender of a full page from :slug, or a short "not found" with a way back. */
export function WithLender({ children }: { children: (lender: Lender) => React.ReactNode }) {
  const { slug } = useParams()
  const lender = useHubStore((s) => s.lenders.find((l) => l.slug === slug))
  if (!lender) {
    return (
      <div className="flex h-screen items-center justify-center bg-canvas p-6 text-sm dark:bg-canvas-dark">
        <div className="card text-center">No lender “{slug}”. <Link to="/lenders" className="text-primary underline">All lenders</Link></div>
      </div>
    )
  }
  return <>{children(lender)}</>
}
